/**
 * Broadcast tick state — `sdk.broadcasts.receipts()`.
 *
 *   const { statusById, refresh } = useBroadcastReceipts(broadcastId, wireMessages)
 *
 * A broadcast list thread has exactly ONE participant (you), so the normal
 * cursor-based tick in `wireToUiMessage` can only ever say `sent`. The real
 * receipts live on each recipient's DM copy; the server aggregates them per
 * message and this hook maps them to WhatsApp's all-or-nothing status:
 * ✓✓ once every recipient has it, blue once every recipient has read it.
 *
 * Fetched on open (and on demand via `refresh`) rather than pushed live — a
 * list owner checks receipts when they look, not per keystroke.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Message as WireMessage } from '../services/messenger.wire'
import type { BroadcastMessageReceipts, MessageStatus } from '@/sdk/instavcMessenger'
import { fetchBroadcastReceipts, broadcastStatusFor } from '@/sdk/instavcMessenger-instance'
import { useAuthStore } from '@/features/auth/store/auth.store'

interface UseBroadcastReceipts {
  /** messageId → aggregate tick state. Absent = not fetched / no ledger. */
  statusById: Map<string, MessageStatus>
  /** messageId → raw counts, for "Read by 12 of 24". */
  receiptsById: Map<string, BroadcastMessageReceipts>
  refresh: () => Promise<void>
}

const EMPTY = new Map<string, never>()

export function useBroadcastReceipts(
  broadcastId: string | null,
  wireMessages: WireMessage[],
): UseBroadcastReceipts {
  const myUserId = useAuthStore((s) => s.user?.id)
  const [receiptsById, setReceiptsById] = useState<Map<string, BroadcastMessageReceipts>>(new Map())

  // Only my own non-system messages can have receipts. Stable key so the fetch
  // fires when the message SET changes, not on every re-render.
  const messageIds = useMemo(
    () => wireMessages.filter((m) => m.type !== 'system').map((m) => m.id),
    [wireMessages],
  )
  const idsKey = messageIds.join(',')

  const refresh = useCallback(async () => {
    if (!broadcastId || !myUserId || messageIds.length === 0) return
    try {
      const rows = await fetchBroadcastReceipts(myUserId, broadcastId, messageIds)
      setReceiptsById(new Map(rows.map((r) => [r.messageId, r])))
    } catch {
      // Receipts are decorative — a failure leaves ticks at `sent` rather than
      // breaking the thread.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastId, myUserId, idsKey])

  useEffect(() => {
    if (!broadcastId) {
      setReceiptsById(new Map())
      return
    }
    void refresh()
  }, [broadcastId, refresh])

  const statusById = useMemo(() => {
    if (!myUserId || receiptsById.size === 0) return EMPTY as Map<string, MessageStatus>
    const out = new Map<string, MessageStatus>()
    for (const [id, r] of receiptsById) out.set(id, broadcastStatusFor(myUserId, r))
    return out
  }, [receiptsById, myUserId])

  return { statusById, receiptsById, refresh }
}
